import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useConfirmInvite } from '../hooks/useConfirmInvite';
import { useNotification } from '../context/NotificationContext';
import ConsentLGPDCheckbox from './components/ConsentLGPDCheckbox';

export default function ConfirmInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirmInvite, loading, error: submitError } = useConfirmInvite();
  const { success: showSuccess, error: showError } = useNotification();

  const token = searchParams.get('token') || '';
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [consentimento, setConsentimento] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      const msg = 'Token de convite não fornecido. Verifique o link no seu email.';
      setError(msg);
      showError(msg);
    }
  }, [token, showError]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 12) return 'Senha deve ter no mínimo 12 caracteres';
    if (!/[A-Z]/.test(pwd)) return 'Senha deve conter pelo menos uma letra maiúscula';
    if (!/[0-9]/.test(pwd)) return 'Senha deve conter pelo menos um número';
    if (!/[!@#$%^&*]/.test(pwd)) return 'Senha deve conter pelo menos um caractere especial (!@#$%^&*)';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validações
    if (!token) {
      const msg = 'Token de convite não fornecido';
      setError(msg);
      showError(msg);
      return;
    }

    if (!senha) {
      const msg = 'Senha é obrigatória';
      setError(msg);
      showError(msg);
      return;
    }

    const passwordError = validatePassword(senha);
    if (passwordError) {
      setError(passwordError);
      showError(passwordError);
      return;
    }

    if (senha !== confirmarSenha) {
      const msg = 'As senhas não conferem';
      setError(msg);
      showError(msg);
      return;
    }

    if (!consentimento) {
      const msg = 'Você deve aceitar a Política de Privacidade e Proteção de Dados';
      setError(msg);
      showError(msg);
      return;
    }

    try {
      await confirmInvite(token, senha, consentimento);
      showSuccess('Sua conta foi ativada com sucesso! Você será redirecionado para o login...');
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      const msg = err.message || 'Erro ao confirmar convite';
      setError(msg);
      showError(msg);
    }
  };

  if (success) {
    return (
      <div className="confirm-invite-page">
        <div className="success-container">
          <h1>✓ Convite Confirmado!</h1>
          <p>Sua conta foi ativada com sucesso. Você será redirecionado para o login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="confirm-invite-page">
      <div className="invite-container">
        <h1>Ativar Sua Conta</h1>
        <p>Defina sua senha para completar o registro no Gestão Creche.</p>

        <form onSubmit={handleSubmit}>
          <label>
            Senha
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Mínimo 12 caracteres, 1 maiúscula, 1 número, 1 especial"
              disabled={loading}
              required
            />
            {senha && (
              <small className="password-hint">
                ✓ Incluir: maiúsculas (A-Z), números (0-9), especiais (!@#$%^&*)
              </small>
            )}
          </label>

          <label>
            Confirmar Senha
            <input
              type="password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              placeholder="Repita a senha"
              disabled={loading}
              required
            />
          </label>

          <div className="divider" />

          <ConsentLGPDCheckbox
            checked={consentimento}
            onChange={setConsentimento}
            required
          />

          {error && <div className="error-message">{error}</div>}
          {submitError && <div className="error-message">{submitError}</div>}

          <button type="submit" disabled={loading} className="btn-primary btn-block">
            {loading ? 'Confirmando...' : 'Ativar Conta'}
          </button>
        </form>

        <div className="form-footer">
          <p>Já tem uma conta? <a href="/login">Faça login</a></p>
        </div>
      </div>
    </div>
  );
}
